import { MOVE_VERTEX_DISTANCE_DECAY, VERTEX_FAMILY_PRIORITY } from './config';
import { VertexFamily } from './types';

/**
 * Length and move snapping both have classes that implement this interface. A "candidate" is a possible snapping
 *   adjustment that could be applied to the polygon(s) being snapped.
 *
 * Each snapping implementation adds other fields needed for their internal logic, but these are the three essentials:
 *
 * `family` is the type of snap that produced the candidate.
 * `distance` is the actual distance delta of the possible snap on the relevant axis.
 * `weight` is a more subjective measurement used when choosing between many possible candidates. It is calculated using
 *   the 2D distance (not just on the snap axis) and decays over long distances as well so a spatially nearby target
 *   can outrank a closer on-axis target for better results.
 */
export interface RankedVertexCandidate {
  readonly family: VertexFamily;
  readonly weight: number;
  readonly distance: number;
}

export function getVertexCandidateWeight(distance: number): number {
  return 1 - distance * MOVE_VERTEX_DISTANCE_DECAY;
}

export function isHigherPriorityCandidate<T extends RankedVertexCandidate>(a: T, b: T): boolean {
  const priorityA = VERTEX_FAMILY_PRIORITY[a.family];
  const priorityB = VERTEX_FAMILY_PRIORITY[b.family];

  if (priorityA !== priorityB) {
    return priorityA > priorityB;
  }

  if (a.weight !== b.weight) {
    return a.weight > b.weight;
  }

  return a.distance < b.distance;
}

export function chooseHigherPriorityCandidate<T extends RankedVertexCandidate>(a: T | null, b: T | null): T | null {
  if (a == null) {
    return b;
  }

  if (b == null) {
    return a;
  }

  return isHigherPriorityCandidate(a, b) ? a : b;
}

export function chooseClosestByDistance<T extends { readonly distance: number }>(
  best: T | null,
  candidate: T | null,
): T | null {
  if (candidate == null) return best;

  if (best == null || candidate.distance < best.distance) {
    return candidate;
  }

  return best;
}
